import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, TextField } from "@mui/material";
import { FC, ReactNode, useEffect } from "react";
import * as yup from "yup";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormattedMessage } from "react-intl";
import CloseIcon from "@mui/icons-material/Close";
import { NameFormData } from "../ViewSelectorTypes";
import { isValidLength, isValidRegex } from "../../../../../validators/validators";
import { LETTERS_NUMBERS_DASH_UNDERSCORE_SPACE_REGEX } from "../../../../../validators/validatorConstants";
import { fieldVariant } from "../../../../../utils/AppUtils";
import { onCloseUnlessEscape } from "../../../../../utils/dialogUtils";

interface SetNameDialogProps {
    open: boolean;
    onClose: VoidFunction;
    onSubmit: (data: any) => Promise<void>;
    onCancel: VoidFunction;
    initData?: NameFormData;
    dialogTitle: ReactNode;
}

const configNameShape = {
    name: yup
        .string()
        .required("View name is required")
        .test("length", "View name can be between 3 and 64 characters long", (value) => {
            if (value) {
                return !isValidLength(value, 3, 64);
            }
            return true;
        })
        .test("is-valid", "View name should contain only alphanumeric characters, space, _, -, '.", (val) => {
            if (val) {
                return !isValidRegex(val, LETTERS_NUMBERS_DASH_UNDERSCORE_SPACE_REGEX);
            }
            return true;
        }),
};

const SetNameDialog: FC<SetNameDialogProps> = ({ open, onClose, onCancel, onSubmit, initData, dialogTitle }) => {
    const { control, reset, handleSubmit } = useForm<NameFormData>({
        resolver: yupResolver(yup.object().shape({ ...configNameShape })),
        mode: "onBlur",
        reValidateMode: "onBlur",
        criteriaMode: "all",
        defaultValues: {
            name: "",
        },
    });

    const submitForm = (data: NameFormData) => {
        onSubmit(data).then(() => reset({ name: "" }));
    };

    const close = () => {
        if (!initData) {
            reset({ name: "" });
        } else {
            reset({ name: initData.name });
        }

        onClose();
    };

    const cancel = () => {
        if (!initData) {
            reset({ name: "" });
        } else {
            reset({ name: initData.name });
        }

        onCancel();
    };

    useEffect(() => {
        if (!initData) {
            reset({ name: "" });
            return;
        }

        reset({ name: initData.name });
    }, [initData]);

    return (
        <Dialog open={open} onClose={onCloseUnlessEscape(close)} fullWidth maxWidth="sm">
            <DialogTitle sx={{ maxWidth: "90%" }}>{dialogTitle}</DialogTitle>
            <form>
                <DialogContent dividers sx={{ paddingBottom: 0 }}>
                    <Controller
                        control={control}
                        name="name"
                        render={({ field, fieldState: { invalid, error } }) => (
                            <TextField
                                {...field}
                                required
                                margin="dense"
                                label={<FormattedMessage id="common.field-label.name" defaultMessage="Name" />}
                                placeholder="Name"
                                fullWidth
                                variant={fieldVariant}
                                helperText={
                                    invalid && error ? (
                                        <FormattedMessage
                                            id={`generic-pro-grid.set-name-dialog.view-name.error.${error.type}`}
                                            defaultMessage={error.message}
                                        />
                                    ) : (
                                        " "
                                    )
                                }
                                error={invalid}
                            />
                        )}
                    />
                </DialogContent>
                <DialogActions sx={{ position: "absolute", right: "0px", top: ".8%" }}>
                    <IconButton onClick={cancel}>
                        <CloseIcon sx={(theme) => ({ color: theme.palette.softBlack?.main })} />
                    </IconButton>
                </DialogActions>
                <DialogActions>
                    <Button onClick={cancel}>
                        <FormattedMessage id="common.button-label.cancel" defaultMessage="Cancel" />
                    </Button>
                    <Button onClick={handleSubmit(submitForm)} type="submit">
                        <FormattedMessage id="common.button-label.confirm" defaultMessage="Confirm" />
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default SetNameDialog;
